import { FlexCenVer } from '@/UI/styles';
import { EditableTimeInput } from './EditableTimeInput';
import { FrameStepButton } from './FrameStepButton';
import { PlayPauseButton } from './PlayPauseButton';
import { SoundButton } from './SoundButton';
import type { MediaPlayerControlsRowProps } from './types';

const slotWidth = 100;
const rowHeight = 24;
const glyphSize = 17;

/**
 * The transport row shared by every player-controls layout: editable timecode on the left, frame
 * step / play / frame step centered, sound on the right. Fixed-width side slots are what keep the
 * transport buttons optically centered regardless of how wide the timecode reads.
 */
export function MediaPlayerControlsRow({
  currentTime,
  duration,
  fps,
  isPaused: paused,
  isMuted: muted,
  hasAudio,
  showFrameStepButtons = true,
  timeInputVariant = 'timecode',
  timeDisplayMode,
  onTimeDisplayModeChange,
  onTogglePlay,
  onSetTime,
  onToggleMute,
}: MediaPlayerControlsRowProps) {
  return (
    <FlexCenVer sx={{ justifyContent: 'space-between', height: `${rowHeight}px`, px: 0.5, width: '100%' }}>
      <FlexCenVer sx={{ gap: 1, width: `${slotWidth}px` }}>
        <EditableTimeInput
          currentTime={currentTime}
          duration={duration}
          fps={fps}
          variant={timeInputVariant}
          mode={timeDisplayMode}
          onModeChange={onTimeDisplayModeChange}
          paused={paused}
          onTogglePlay={onTogglePlay}
          onSetTime={onSetTime}
        />
      </FlexCenVer>

      <FlexCenVer sx={{ gap: 1 }}>
        {showFrameStepButtons ? (
          <FrameStepButton
            direction="back"
            currentTime={currentTime}
            duration={duration}
            fps={fps}
            paused={paused}
            onTogglePlay={onTogglePlay}
            onSetTime={onSetTime}
            width={rowHeight}
            height={rowHeight}
            btnW={glyphSize}
            btnH={glyphSize}
          />
        ) : null}
        <PlayPauseButton
          onTogglePlay={onTogglePlay}
          paused={paused}
          width={rowHeight}
          height={rowHeight}
          btnW={glyphSize}
          btnH={glyphSize}
        />
        {showFrameStepButtons ? (
          <FrameStepButton
            direction="forward"
            currentTime={currentTime}
            duration={duration}
            fps={fps}
            paused={paused}
            onTogglePlay={onTogglePlay}
            onSetTime={onSetTime}
            width={rowHeight}
            height={rowHeight}
            btnW={glyphSize}
            btnH={glyphSize}
          />
        ) : null}
      </FlexCenVer>

      <FlexCenVer sx={{ width: `${slotWidth}px`, justifyContent: 'flex-end' }}>
        <SoundButton
          onToggleMute={onToggleMute}
          hasAudio={hasAudio}
          muted={muted}
          width={rowHeight}
          height={rowHeight}
          btnW={glyphSize}
          btnH={glyphSize}
        />
      </FlexCenVer>
    </FlexCenVer>
  );
}
